import { IS_LITE } from '../core/edition';
import * as THREE from 'three';
import { GeometryBuilder } from './geometry';
import { riverX, type City } from '../world/city';
import { CELL, GROUND, type Settings } from '../core/config';
import { terrainGeometry } from './terrain';

export class View {
  renderer: THREE.WebGLRenderer;
  scene = new THREE.Scene();
  camera = new THREE.PerspectiveCamera(46, 1, 1, 1100);
  baseMaterial = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.83, metalness: 0.025 });
  glassMaterial = new THREE.MeshStandardMaterial({
    vertexColors: true,
    roughness: 0.17,
    metalness: 0.52,
    envMapIntensity: 1.2,
  });
  nearGlassMaterial = new THREE.MeshStandardMaterial({
    color: 0xbddbd7,
    roughness: 0.14,
    metalness: 0.1,
    envMapIntensity: 0.55,
    transparent: true,
    opacity: 0.23,
    depthWrite: false,
  });
  material: THREE.Material[] = [this.baseMaterial, this.glassMaterial, this.nearGlassMaterial];
  sun = new THREE.DirectionalLight(0xfff1da, 2.7);
  canvas: HTMLCanvasElement;
  lost = false;
  private ground = new THREE.Group();
  private waterMaterial = new THREE.MeshStandardMaterial({
    color: 0x78afb2,
    roughness: 0.24,
    metalness: 0.38,
    envMapIntensity: 0.8,
  });
  private environment!: THREE.WebGLRenderTarget;
  private shadowOffset = new THREE.Vector3(-85, 160, 85);
  constructor(container: HTMLElement) {
    this.renderer = new THREE.WebGLRenderer({ antialias: !IS_LITE, powerPreference: 'high-performance' });
    this.canvas = this.renderer.domElement;
    this.canvas.id = 'game';
    this.canvas.setAttribute('aria-label', 'City demolition game');
    this.canvas.tabIndex = 0;
    container.prepend(this.canvas);
    this.renderer.setClearColor(0xbad4db);
    this.scene.background = new THREE.Color(0xbad4db);
    this.scene.fog = new THREE.Fog(0xbad4db, 300, 920);
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 0.99;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    this.scene.add(new THREE.HemisphereLight(0xe8f4ff, 0x9ca386, 1.15));
    this.sun.position.copy(this.shadowOffset);
    this.sun.castShadow = true;
    this.sun.shadow.camera.left = -115;
    this.sun.shadow.camera.right = 115;
    this.sun.shadow.camera.top = 115;
    this.sun.shadow.camera.bottom = -115;
    this.sun.shadow.camera.near = 2;
    this.sun.shadow.camera.far = 380;
    this.sun.shadow.bias = -0.00015;
    this.sun.shadow.normalBias = 0.08;
    this.sun.shadow.radius = 2;
    this.scene.add(this.sun, this.sun.target, this.ground);
    this.prepareEnvironment();
  }
  private prepareEnvironment(): void {
    // Local sky reflections: opaque PBR glass, without transparency layers or ray tracing.
    const faces = Array.from({ length: 6 }, (_, face) => {
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = 128;
      const ctx = canvas.getContext('2d')!;
      const gradient = ctx.createLinearGradient(0, 0, 0, 128);
      gradient.addColorStop(0, face === 2 ? '#cfe5eb' : '#91b9cb');
      gradient.addColorStop(0.46, '#e5ede4');
      gradient.addColorStop(0.53, '#aeb9a5');
      gradient.addColorStop(1, '#788c7d');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, 128, 128);
      ctx.fillStyle = 'rgba(250,251,238,0.62)';
      ctx.fillRect(12 + face * 5, 28, 49, 9);
      ctx.fillStyle = 'rgba(248,249,237,0.24)';
      ctx.fillRect(71, 42, 53, 8);
      return canvas;
    });
    const cube = new THREE.CubeTexture(faces);
    cube.colorSpace = THREE.SRGBColorSpace;
    cube.needsUpdate = true;
    const generator = new THREE.PMREMGenerator(this.renderer);
    this.environment = generator.fromCubemap(cube);
    this.scene.environment = this.environment.texture;
    this.scene.environmentIntensity = 0.42;
    this.baseMaterial.envMapIntensity = 0.42;
    generator.dispose();
    cube.dispose();
    const normals = new Uint8Array(64 * 64 * 4);
    for (let y = 0; y < 64; y++)
      for (let x = 0; x < 64; x++) {
        const p = (x + y * 64) * 4;
        normals[p] = 128 + Math.round(Math.sin(x * 0.8 + y * 0.24) * 14);
        normals[p + 1] = 128 + Math.round(Math.sin(y * 0.73 + x * 0.11) * 12);
        normals[p + 2] = 252;
        normals[p + 3] = 255;
      }
    const map = new THREE.DataTexture(normals, 64, 64, THREE.RGBAFormat);
    map.wrapS = map.wrapT = THREE.RepeatWrapping;
    map.magFilter = THREE.LinearFilter;
    map.minFilter = THREE.LinearFilter;
    map.needsUpdate = true;
    this.waterMaterial.normalMap = map;
    this.waterMaterial.normalScale.set(0.32, 0.32);
  }
  configure(s: Settings, factor = 1): void {
    if (this.scene.fog instanceof THREE.Fog) {
      // The regional overview is farther away than the old fog far plane.
      // Keep the actual skyline and coast visible instead of filling that
      // legitimate camera view with a uniform fog colour.
      this.scene.fog.near = s.sceneryDistance * 2;
      this.scene.fog.far = s.sceneryDistance * 3.5;
    }
    const shadowChanged = this.renderer.shadowMap.enabled !== s.shadows > 0;
    // Scale means physical output size. No automatic desktop supersampling.
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1) * s.scale * factor);
    this.renderer.shadowMap.enabled = s.shadows > 0;
    if (shadowChanged) {
      const materials = new Set<THREE.Material>();
      this.scene.traverse((o) => {
        if (o instanceof THREE.Mesh)
          for (const m of Array.isArray(o.material) ? o.material : [o.material]) materials.add(m);
      });
      for (const m of materials) m.needsUpdate = true;
    }
    if (this.sun.shadow.mapSize.x !== Math.max(512, s.shadows)) {
      this.sun.shadow.mapSize.setScalar(Math.max(512, s.shadows));
      this.sun.shadow.map?.dispose();
      this.sun.shadow.map = null;
    }
    this.resize();
  }
  resize(): void {
    const w = window.innerWidth,
      h = window.innerHeight;
    this.renderer.setSize(w, h, false);
    this.renderer.resetState();
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }
  update(target: { x: number; y: number; z: number }, time: number): void {
    this.sun.target.position.set(target.x, 0, target.z);
    this.sun.position.set(
      target.x + this.shadowOffset.x,
      this.shadowOffset.y,
      target.z + this.shadowOffset.z,
    );
    if (this.waterMaterial.normalMap) {
      this.waterMaterial.normalMap.offset.x = time * 0.018;
      this.waterMaterial.normalMap.offset.y = time * 0.009;
    }
  }
  render(): void {
    if (!this.lost) this.renderer.render(this.scene, this.camera);
  }
  scenery(city: City): void {
    const height = city.buildings.reduce((top, b) => Math.max(top, (b.baseY ?? 0.16) + b.h * CELL), 0);
    const scale = Math.max(1, (height + 80) / 160);
    // Move the light back along the same direction so tall casters stay inside
    // the depth range. Keep the requested map size and near-ground texel density.
    this.shadowOffset.set(-85 * scale, 160 * scale, 85 * scale);
    this.sun.shadow.camera.far = 380 * scale;
    this.sun.shadow.camera.updateProjectionMatrix();
    for (const o of [...this.ground.children]) {
      this.ground.remove(o);
      if (o instanceof THREE.Mesh) o.geometry.dispose();
    }
    const terrain = city.terrain ? terrainGeometry(city.terrain) : undefined,
      g = terrain?.land ?? new GeometryBuilder(),
      water = terrain?.water ?? new GeometryBuilder();
    const h = city.half;
    // Only terrain, road surfaces and water are static. Park structures and trees live in City.props.
    if (!city.terrain) {
      g.box(-660, -0.75, -660, 1320, 0.08, 1320, 0xa9b998);
      g.box(-h, -0.66, -h, h * 2, 0.67, h * 2, 0xa7b58e);
    }
    if (city.roadGraph) {
      for (const b of city.buildings) {
        const garden = ['suburbs', 'rural', 'outer-residential', 'farmland'].includes(b.district ?? '');
        g.box(
          b.x - 1.2,
          city.infrastructure ? GROUND + 0.01 : 0.018,
          b.z - 1.2,
          b.w * 1.25 + 2.4,
          0.028,
          b.d * 1.25 + 2.4,
          garden ? 0xaabd89 : 0xd3d2bd,
        );
      }
      for (const parcel of city.landscape?.parcels ?? []) {
        g.box(parcel.x, GROUND + 0.003, parcel.z, parcel.w, 0.003, parcel.d, parcel.color);
        if (parcel.kind === 'field')
          for (let z = parcel.z + 0.65; z < parcel.z + parcel.d; z += 3.75)
            g.box(parcel.x + 0.2, GROUND + 0.007, z, parcel.w - 0.4, 0.002, 0.28, 0x9c916f);
      }
      for (const [x, z, w, d, color] of [
        [116, 116, 32, 39, 0xb7be86],
        [157, 116, 36, 13, 0xc8bc84],
        [118, 161, 71, 21, 0xbac093],
      ]) {
        g.box(x, GROUND + 0.003, z, w, 0.003, d, color);
        for (let zz = z + 1.2; zz < z + d; zz += 1.65)
          g.box(x + 0.4, GROUND + 0.007, zz, w - 0.8, 0.002, 0.2, 0xa6ae79);
      }
      g.box(-56, GROUND + 0.003, -57, 35, 0.003, 37, 0xa3b67f);
      const river = city.river!;
      for (let i = 0; !city.terrain && i < river.points.length - 1; i++) {
        const a = river.points[i],
          b = river.points[i + 1],
          width = river.width / 2;
        if (city.infrastructure) {
          for (const side of [-1, 1])
            g.quad(
              { x: side < 0 ? -h : a.x + width + 3.5, y: GROUND, z: a.z },
              { x: side < 0 ? -h : b.x + width + 3.5, y: GROUND, z: b.z },
              { x: side < 0 ? b.x - width - 3.5 : h, y: GROUND, z: b.z },
              { x: side < 0 ? a.x - width - 3.5 : h, y: GROUND, z: a.z },
              { x: 0, y: 1, z: 0 },
              0xa7b58e,
            );
        }
        for (const [extra, yy, tint] of [
          [3.5, 0.04, 0xc6c6a1],
          [1.1, 0.046, 0x95ad8a],
        ])
          g.quad(
            { x: a.x - width - extra, y: yy, z: a.z },
            { x: b.x - width - extra, y: yy, z: b.z },
            { x: b.x + width + extra, y: yy, z: b.z },
            { x: a.x + width + extra, y: yy, z: a.z },
            { x: 0, y: 1, z: 0 },
            tint,
          );
        water.quad(
          { x: a.x - width, y: 0.061, z: a.z },
          { x: b.x - width, y: 0.061, z: b.z },
          { x: b.x + width, y: 0.061, z: b.z },
          { x: a.x + width, y: 0.061, z: a.z },
          { x: 0, y: 1, z: 0 },
          0xffffff,
        );
      }
      for (const e of city.infrastructure ? [] : city.roadGraph.edges) {
        const a = city.roadGraph.nodes[e.from],
          b = city.roadGraph.nodes[e.to];
        if (e.bridgeId !== undefined) {
          const left = riverX(a.z) - 17.5,
            right = left + 35;
          this.road(g, a.x, a.z, left, a.z, e.width, 0.135, 5.16);
          this.road(g, right, a.z, b.x, b.z, e.width, 5.16, 0.135);
        } else this.road(g, a.x, a.z, b.x, b.z, e.width);
      }
      for (const n of city.roadGraph.nodes)
        if (n.x < 100 || n.z < 80)
          for (let i = -2.5; i < 3; i += 0.8) {
            g.box(
              n.x + i,
              city.infrastructure ? (n.y ?? GROUND) + 0.012 : 0.153,
              n.z + 5.0,
              0.38,
              0.012,
              1.6,
              0xece6d4,
            );
            g.box(
              n.x + 5.0,
              city.infrastructure ? (n.y ?? GROUND) + 0.012 : 0.153,
              n.z + i,
              1.6,
              0.012,
              0.38,
              0xece6d4,
            );
          }
    } else {
      for (const r of city.roads) {
        this.road(g, r, -h, r, h, 7.1);
        this.road(g, -h, r, h, r, 7.1);
      }
    }
    const mesh = new THREE.Mesh(g.geometry(), this.baseMaterial);
    mesh.receiveShadow = true;
    this.ground.add(mesh);
    if (water.positions.length) {
      const geometry = water.geometry(),
        uv: number[] = [];
      for (let i = 0; i < water.positions.length; i += 3)
        uv.push(water.positions[i] / 11, water.positions[i + 2] / 11);
      geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
      const riverMesh = new THREE.Mesh(geometry, this.waterMaterial);
      riverMesh.receiveShadow = true;
      this.ground.add(riverMesh);
    }
  }
  private road(
    g: GeometryBuilder,
    x: number,
    z: number,
    X: number,
    Z: number,
    width: number,
    y = 0.135,
    Y = y,
  ): void {
    const length = Math.hypot(X - x, Z - z),
      nx = -(Z - z) / length,
      nz = (X - x) / length;
    const strip = (half: number, yy: number, YY: number, color: number) =>
      g.quad(
        { x: x + nx * half, y: yy, z: z + nz * half },
        { x: X + nx * half, y: YY, z: Z + nz * half },
        { x: X - nx * half, y: YY, z: Z - nz * half },
        { x: x - nx * half, y: yy, z: z - nz * half },
        { x: 0, y: 1, z: 0 },
        color,
      );
    strip(width / 2 + 1.0, y - 0.023, Y - 0.023, 0xd4d2bd);
    strip(width / 2, y, Y, 0x74817f);
    for (let s = 5; s < length - 5; s += 6) {
      const a = s / length,
        b = Math.min(length - 5, s + 2.4) / length;
      g.quad(
        { x: x + (X - x) * a + nx * 0.065, y: y + (Y - y) * a + 0.012, z: z + (Z - z) * a + nz * 0.065 },
        { x: x + (X - x) * b + nx * 0.065, y: y + (Y - y) * b + 0.012, z: z + (Z - z) * b + nz * 0.065 },
        { x: x + (X - x) * b - nx * 0.065, y: y + (Y - y) * b + 0.012, z: z + (Z - z) * b - nz * 0.065 },
        { x: x + (X - x) * a - nx * 0.065, y: y + (Y - y) * a + 0.012, z: z + (Z - z) * a - nz * 0.065 },
        { x: 0, y: 1, z: 0 },
        0xe0d4ac,
      );
    }
  }
}
